/* eslint-disable no-console */
import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { actualizarMontoCuota, liquidarCredito } from "../../utils/creditos";
import { getLocale } from "../../utils/getLocale";
import { useCurrency } from "../../utils/useCurrency";
import styles from "./Creditos.module.scss";

const TIPO_ACREEDOR_LABEL = {
  banco: "Banco",
  privado: "Privado",
  casa_matriz: "Casa matriz",
  intercompany: "Intercompany",
  socio: "Socio",
  contraparte: "Contraparte",
};

const PERIODICIDAD_LABEL = {
  mensual: "Mensual",
  trimestral: "Trimestral",
  semestral: "Semestral",
  anual: "Anual",
  unica: "Pago único",
};

const hoyISO = () => new Date().toISOString().split("T")[0];

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [y, m, d] = f.split("-");
  return `${d}/${m}/${y}`;
};

const DetalleCreditoModal = ({ credito, onClose, onRefresh, onEdit }) => {
  const { formatAmount } = useCurrency();
  const isOpen = !!credito;
  const moneda = credito?.moneda || "CLP";
  const pais = credito?.pais || "Chile";
  // Comparación por campo `direccion` (10.7), nunca por nombre.
  const esOtorgado = credito?.direccion === "otorgado";
  // La edición a favor solo se ofrece si el recupero sigue pendiente (no reconciliado — Fase 5).
  const recuperoPendiente = esOtorgado && (credito?.cuotas || []).every((c) => c.estado !== "pagada");

  // Edición manual de montos inline sólo si el crédito no tiene cuota fija. Para 'otorgado' se
  // desactiva: su monto/fecha se editan por el modal "Editar" (sincroniza header ↔ cuota), no
  // inline — así no se reintroduce el desync que arreglamos (monto_original viejo / capital ≠ total).
  const editable = credito && !esOtorgado && (credito.monto_cuota_fijo == null || credito.monto_cuota_fijo === "");

  // Copia local de los montos editables, keyed por cuota id.
  const [montos, setMontos] = useState({});
  const [liquidando, setLiquidando] = useState(false);

  useEffect(() => {
    if (!credito) return;
    const m = {};
    (credito.cuotas || []).forEach((c) => {
      m[c.id] = c.monto_total;
    });
    setMontos(m);
  }, [credito]);

  const cuotas = useMemo(() => credito?.cuotas || [], [credito]);
  const hoy = hoyISO();

  const resumen = useMemo(() => {
    let pagado = 0;
    let pendiente = 0;
    cuotas.forEach((c) => {
      if (c.estado === "pagada") pagado += Number(c.monto_total || 0);
      else pendiente += Number(c.monto_total || 0);
    });
    return { pagado, pendiente };
  }, [cuotas]);

  const handleBlurMonto = async (cuota) => {
    const nuevo = Number(montos[cuota.id]);
    if (isNaN(nuevo) || nuevo === Number(cuota.monto_total)) return;
    try {
      await actualizarMontoCuota(cuota.id, nuevo);
      toast.success(`Cuota ${cuota.numero_cuota} actualizada`);
      onRefresh?.();
    } catch (err) {
      console.error("Error actualizando cuota:", err);
      toast.error("Error al actualizar la cuota");
    }
  };

  const handleLiquidar = async () => {
    if (!window.confirm(`¿Marcar el crédito "${credito.nombre}" como liquidado?`)) return;
    setLiquidando(true);
    try {
      await liquidarCredito(credito.id);
      toast.success("Crédito liquidado");
      onRefresh?.();
      onClose();
    } catch (err) {
      console.error("Error liquidando crédito:", err);
      toast.error("Error al liquidar el crédito");
    } finally {
      setLiquidando(false);
    }
  };

  if (!isOpen) return null;

  const dato = (label, value) => (
    <div className={styles.detalleItem}>
      <span className={styles.detalleItemLabel}>{label}</span>
      <span className={styles.detalleItemValue}>{value}</span>
    </div>
  );

  return (
    <Modal show={isOpen} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>{credito.nombre}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ── Cabecera de datos (solo lectura). Para 'otorgado' se relabela y se ocultan los
             campos de deuda que no aplican; el display de deuda queda idéntico. ── */}
        <div className={styles.detalleHeader}>
          {dato("Dirección", esOtorgado ? "A favor (por cobrar / a recuperar)" : "Tomado (deuda)")}
          {dato(esOtorgado ? "Contraparte" : "Acreedor", credito.acreedor_nombre)}
          {dato("Tipo", TIPO_ACREEDOR_LABEL[credito.tipo_acreedor] || credito.tipo_acreedor)}
          {dato(esOtorgado ? "Monto" : "Monto original", formatAmount(credito.monto_original, moneda))}
          {dato("Moneda", moneda)}
          {!esOtorgado &&
            dato(
              "Tasa",
              credito.tasa_interes != null
                ? `${credito.tasa_interes}% ${credito.tipo_tasa ? `(${credito.tipo_tasa})` : ""}`
                : "—"
            )}
          {!esOtorgado && dato("Periodicidad", PERIODICIDAD_LABEL[credito.periodicidad] || credito.periodicidad)}
          {!esOtorgado && dato("N° cuotas", credito.num_cuotas)}
          {!esOtorgado && dato("Día de pago", credito.dia_pago || "—")}
          {dato(esOtorgado ? "Fecha de entrega" : "Desembolso", formatFecha(credito.fecha_desembolso))}
          {dato(esOtorgado ? "Fecha de recupero" : "Primera cuota", formatFecha(credito.fecha_primera_cuota))}
          {!esOtorgado && dato("Cuotas de gracia", credito.cuotas_gracia || 0)}
          {!esOtorgado && dato("Pago inicial", formatAmount(credito.pago_inicial || 0, moneda))}
          {dato(
            "Estado",
            <span
              className={`${styles.estadoBadge} ${
                credito.estado === "liquidado" ? styles.estadoLiquidado : styles.estadoActivo
              }`}
            >
              {credito.estado === "liquidado" ? "Liquidado" : "Activo"}
            </span>
          )}
          {credito.notas ? dato("Notas", credito.notas) : null}
        </div>

        {/* ── Tabla de cuotas ── */}
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          <table className={styles.cuotasTable}>
            <thead>
              <tr>
                <th style={{ width: "8%" }}>N°</th>
                <th style={{ width: "16%" }}>Vencimiento</th>
                <th style={{ width: "20%" }}>Monto</th>
                <th style={{ width: "14%" }}>Estado</th>
                <th style={{ width: "16%" }}>Pagada</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {cuotas.map((c) => {
                const pagada = c.estado === "pagada";
                const vencida = !pagada && c.fecha_vencimiento < hoy;
                const fechaPagoReal = c.movimiento?.fecha || c.fecha_pago_real || null;
                return (
                  <tr key={c.id} className={pagada ? styles.cuotaPagada : undefined}>
                    <td style={{ textAlign: "center" }}>{c.numero_cuota}</td>
                    <td style={{ textAlign: "center" }} className={vencida ? styles.cuotaVencida : undefined}>
                      {formatFecha(c.fecha_vencimiento)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {editable && !pagada ? (
                        <input
                          type="text"
                          className={styles.cuotaInputMonto}
                          value={
                            montos[c.id] === "" || montos[c.id] == null
                              ? ""
                              : Number(montos[c.id]).toLocaleString(getLocale(pais), { maximumFractionDigits: 0 })
                          }
                          onChange={(e) => {
                            const clean = e.target.value.replace(/\D/g, "");
                            setMontos((prev) => ({ ...prev, [c.id]: clean === "" ? "" : parseInt(clean, 10) }));
                          }}
                          onBlur={() => handleBlurMonto(c)}
                        />
                      ) : (
                        formatAmount(c.monto_total, moneda)
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span
                        className={`${styles.estadoBadge} ${pagada ? styles.estadoLiquidado : styles.estadoActivo}`}
                      >
                        {pagada ? "Pagada" : "Pendiente"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>{pagada ? formatFecha(fechaPagoReal) : "—"}</td>
                    <td style={{ fontSize: "0.76rem", color: "#666" }}>{c.notas || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Resumen ── */}
        <div className={styles.resumenPie}>
          <span className={styles.resumenPieItem}>
            Total pagado: <strong>{formatAmount(resumen.pagado, moneda)}</strong>
          </span>
          <span className={styles.resumenPieItem}>
            Total pendiente: <strong>{formatAmount(resumen.pendiente, moneda)}</strong>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {esOtorgado && recuperoPendiente && credito.estado !== "liquidado" && onEdit && (
          <Button variant="primary" size="sm" onClick={() => onEdit(credito)} disabled={liquidando}>
            Editar
          </Button>
        )}
        {credito.estado !== "liquidado" && (
          <Button variant="success" size="sm" onClick={handleLiquidar} disabled={liquidando}>
            {liquidando ? "Liquidando..." : "Liquidar crédito"}
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DetalleCreditoModal;
